import React from 'react'

export const EMPTY_MFA_DIGITS: string[] = ['', '', '', '', '', '']

type MfaCodeInputProps = {
  idPrefix: string
  value: string[]
  onChange: (digits: string[]) => void
  /** Called when all 6 digits are filled (e.g. auto-submit on login) */
  onComplete?: (code: string) => void
  disabled?: boolean
}

function focusDigit(idPrefix: string, index: number) {
  document.getElementById(`${idPrefix}-${index}`)?.focus()
}

export function focusMfaCodeInput(idPrefix: string, index = 0) {
  focusDigit(idPrefix, index)
}

export function MfaCodeInput({ idPrefix, value, onChange, onComplete, disabled }: MfaCodeInputProps) {
  const applyDigits = (digits: string[]) => {
    onChange(digits)
    const code = digits.join('')
    if (code.length === 6) {
      onComplete?.(code)
    }
  }

  const onDigitChange = (index: number, rawValue: string) => {
    const numeric = rawValue.replace(/\D/g, '')
    if (!numeric) {
      const next = [...value]
      next[index] = ''
      onChange(next)
      return
    }

    // OTP autofill / paste may deliver multiple digits into one input
    if (numeric.length > 1) {
      const next = [...value]
      for (let i = 0; i < numeric.length && index + i < 6; i++) {
        next[index + i] = numeric[i]!
      }
      applyDigits(next)
      focusDigit(idPrefix, Math.min(index + numeric.length, 5))
      return
    }

    const next = [...value]
    next[index] = numeric
    applyDigits(next)
    if (index < 5) {
      focusDigit(idPrefix, index + 1)
    }
  }

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const next = [...EMPTY_MFA_DIGITS]
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i]!
    }
    applyDigits(next)
    focusDigit(idPrefix, Math.min(pasted.length, 5))
  }

  const onDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      focusDigit(idPrefix, index - 1)
    }
  }

  return (
    <div className='flex gap-2' dir='ltr'>
      {value.map((digit, index) => (
        <input
          key={index}
          id={`${idPrefix}-${index}`}
          type='text'
          inputMode='numeric'
          autoComplete='one-time-code'
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={e => onDigitChange(index, e.target.value)}
          onPaste={onPaste}
          onKeyDown={e => onDigitKeyDown(index, e)}
          className='h-11 w-10 rounded-md border border-gray-300 text-center text-lg focus:border-gray-900 focus:outline-none disabled:opacity-50'
        />
      ))}
    </div>
  )
}
